import type { Dispatch, SetStateAction } from 'react';
import Button from '@components/UI/Button';
import Modal from '@components/UI/Modal';
import FormSelect from '@components/UI/FormSelect';
import { showToast } from '@components/Layout/Toast';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { BacktestSortingRule, TradeDirection, TradingProfile } from '@einhaus/project314-types';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import type { NewBacktestSortingRuleState, SaveTradingProfileSettings } from './types';

interface BacktestSortingRuleModalProps {
    isOpen: boolean;
    setShowBacktestSortingRulesModal: Dispatch<SetStateAction<boolean>>;
    editingBacktestSortingRuleId: string | null;
    setEditingBacktestSortingRuleId: Dispatch<SetStateAction<string | null>>;
    newBacktestSortingRule: NewBacktestSortingRuleState;
    setNewBacktestSortingRule: Dispatch<SetStateAction<NewBacktestSortingRuleState>>;
    backtestSortingRules: BacktestSortingRule[];
    setBacktestSortingRules: Dispatch<SetStateAction<BacktestSortingRule[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    saveBacktestSortingRule: SaveTradingProfileSettings;
}

const BacktestSortingRuleModal = ({
    isOpen,
    setShowBacktestSortingRulesModal,
    editingBacktestSortingRuleId,
    setEditingBacktestSortingRuleId,
    newBacktestSortingRule,
    setNewBacktestSortingRule,
    backtestSortingRules,
    setBacktestSortingRules,
    formData,
    setFormData,
    tradingProfileId,
    saveBacktestSortingRule
}: BacktestSortingRuleModalProps) => {
    // Backtest Sorting Rules Modal handlers
    const closeBacktestSortingRulesModal = () => {
        setShowBacktestSortingRulesModal(false);
        setEditingBacktestSortingRuleId(null);

        setNewBacktestSortingRule({
            field: '',
            order: 'desc',
            tradeDirection: 'either'
        });
    };

    const handleSaveBacktestSortingRule = () => {
        const { field, order, tradeDirection } = newBacktestSortingRule;

        if (!field || !order || !tradeDirection) {
            showToast('Please fill in all backtest sorting rule fields', 'error');
            return;
        }

        const ruleId = editingBacktestSortingRuleId ?? `rule-${getUnixTime()}`;

        const newRule: BacktestSortingRule = {
            id: ruleId,
            field,
            order,
            tradeDirection: tradeDirection as TradeDirection | 'either'
        };

        const updatedRules =
            editingBacktestSortingRuleId == null
                ? [...backtestSortingRules, newRule]
                : backtestSortingRules.map((existing) => (existing.id === ruleId ? newRule : existing));

        setBacktestSortingRules(updatedRules);

        // Update the form data with the new backtest sorting rule
        const updatedFormData = {
            ...formData,
            backtestSortingRules: updatedRules
        };

        setFormData(updatedFormData);
        showSpinner();

        // Use the backtest sorting rule mutation to save the updated settings
        saveBacktestSortingRule(
            {
                tradingProfileId,
                settings: updatedFormData
            },
            {
                onSuccess: () => {
                    hideSpinner();
                    closeBacktestSortingRulesModal();
                },
                onError: () => {
                    hideSpinner();
                }
            }
        );
    };

    return (
        <Modal
            footer={
                <>
                    <Button variant="secondary" onClick={closeBacktestSortingRulesModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveBacktestSortingRule}>
                        {editingBacktestSortingRuleId == null ? 'Add' : 'Save'}
                    </Button>
                </>
            }
            isOpen={isOpen}
            title={editingBacktestSortingRuleId == null ? 'Add Backtest Sorting Rule' : 'Edit Backtest Sorting Rule'}
            onClose={closeBacktestSortingRulesModal}
        >
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="backtestSortingTradeDirection">
                    Position side
                </label>
                <FormSelect
                    id="backtestSortingTradeDirection"
                    options={[
                        { value: 'either', label: 'Either' },
                        { value: 'long', label: 'Long' },
                        { value: 'short', label: 'Short' }
                    ]}
                    value={newBacktestSortingRule.tradeDirection}
                    onChange={(e) => setNewBacktestSortingRule((prev) => ({ ...prev, tradeDirection: e.target.value }))}
                />
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="backtestSortingField">
                    Sort By
                </label>
                <select
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="backtestSortingField"
                    value={newBacktestSortingRule.field}
                    onChange={(e) => setNewBacktestSortingRule((prev) => ({ ...prev, field: e.target.value }))}
                >
                    <option value="">Select Field</option>
                    <optgroup label="Scoring Metrics">
                        <option value="riskAdjustedScore">Risk-Adjusted Score (Conservative)</option>
                        <option value="profitWeightedScore">Profit-Weighted Score (Aggressive)</option>
                    </optgroup>
                    <optgroup label="General Metrics">
                        <option value="avgMonth">Average Month</option>
                        <option value="numTrades">Number of Trades</option>
                        <option value="numTradesLong">Number of Long Trades</option>
                        <option value="numTradesShort">Number of Short Trades</option>
                        <option value="avgTradeProfit">Average Trade Return (%)</option>
                        <option value="avgTradeProfitShort">Average Short Trade Return (%)</option>
                        <option value="avgTradeProfitLong">Average Long Trade Return (%)</option>
                        <option value="profitFactor">Profit Factor</option>
                        <option value="medianProfitPercent">Median Trade Return (%)</option>
                        <option value="medianProfitLongPercent">Median Long Trade Return (%)</option>
                        <option value="medianProfitShortPercent">Median Short Trade Return (%)</option>
                        <option value="returnOnRiskCapital">Return on Risk Capital (%)</option>
                        <option value="avgProfitPerDayPercent">Average Profit/Day (%)</option>
                        <option value="medianProfitPerDayPercent">Median Profit/Day (%)</option>
                        <option value="distinctTradingDays">Distinct Trading Days</option>
                        <option value="activeMonths">Active Months</option>
                        <option value="winRate">Win Rate</option>
                        <option value="winRateLong">Long Win Rate</option>
                        <option value="winRateShort">Short Win Rate</option>
                        <option value="worstMonth">Worst Month</option>
                    </optgroup>

                    <optgroup label="7-Day Metrics">
                        <option value="numTrades7">Number of Trades (7d)</option>
                        <option value="avgGain7">Average Gain (7d)</option>
                        <option value="winRate7">Win Rate (7d)</option>
                        <option value="numTradesLong7">Number of Long Trades (7d)</option>
                        <option value="avgGainLong7">Average Long Gain (7d)</option>
                        <option value="winRateLong7">Long Win Rate (7d)</option>
                        <option value="numTradesShort7">Number of Short Trades (7d)</option>
                        <option value="avgGainShort7">Average Short Gain (7d)</option>
                        <option value="winRateShort7">Short Win Rate (7d)</option>
                    </optgroup>

                    <optgroup label="14-Day Metrics">
                        <option value="numTrades14">Number of Trades (14d)</option>
                        <option value="avgGain14">Average Gain (14d)</option>
                        <option value="winRate14">Win Rate (14d)</option>
                        <option value="numTradesLong14">Number of Long Trades (14d)</option>
                        <option value="avgGainLong14">Average Long Gain (14d)</option>
                        <option value="winRateLong14">Long Win Rate (14d)</option>
                        <option value="numTradesShort14">Number of Short Trades (14d)</option>
                        <option value="avgGainShort14">Average Short Gain (14d)</option>
                        <option value="winRateShort14">Short Win Rate (14d)</option>
                    </optgroup>

                    <optgroup label="30-Day Metrics">
                        <option value="numTrades30">Number of Trades (30d)</option>
                        <option value="avgGain30">Average Gain (30d)</option>
                        <option value="winRate30">Win Rate (30d)</option>
                        <option value="numTradesLong30">Number of Long Trades (30d)</option>
                        <option value="avgGainLong30">Average Long Gain (30d)</option>
                        <option value="winRateLong30">Long Win Rate (30d)</option>
                        <option value="numTradesShort30">Number of Short Trades (30d)</option>
                        <option value="avgGainShort30">Average Short Gain (30d)</option>
                        <option value="winRateShort30">Short Win Rate (30d)</option>
                    </optgroup>

                    <optgroup label="60-Day Metrics">
                        <option value="numTrades60">Number of Trades (60d)</option>
                        <option value="avgGain60">Average Gain (60d)</option>
                        <option value="winRate60">Win Rate (60d)</option>
                        <option value="numTradesLong60">Number of Long Trades (60d)</option>
                        <option value="avgGainLong60">Average Long Gain (60d)</option>
                        <option value="winRateLong60">Long Win Rate (60d)</option>
                        <option value="numTradesShort60">Number of Short Trades (60d)</option>
                        <option value="avgGainShort60">Average Short Gain (60d)</option>
                        <option value="winRateShort60">Short Win Rate (60d)</option>
                    </optgroup>

                    <optgroup label="90-Day Metrics">
                        <option value="numTrades90">Number of Trades (90d)</option>
                        <option value="avgGain90">Average Gain (90d)</option>
                        <option value="winRate90">Win Rate (90d)</option>
                        <option value="numTradesLong90">Number of Long Trades (90d)</option>
                        <option value="avgGainLong90">Average Long Gain (90d)</option>
                        <option value="winRateLong90">Long Win Rate (90d)</option>
                        <option value="numTradesShort90">Number of Short Trades (90d)</option>
                        <option value="avgGainShort90">Average Short Gain (90d)</option>
                        <option value="winRateShort90">Short Win Rate (90d)</option>
                    </optgroup>

                    <optgroup label="120-Day Metrics">
                        <option value="numTrades120">Number of Trades (120d)</option>
                        <option value="avgGain120">Average Gain (120d)</option>
                        <option value="winRate120">Win Rate (120d)</option>
                        <option value="numTradesLong120">Number of Long Trades (120d)</option>
                        <option value="avgGainLong120">Average Long Gain (120d)</option>
                        <option value="winRateLong120">Long Win Rate (120d)</option>
                        <option value="numTradesShort120">Number of Short Trades (120d)</option>
                        <option value="avgGainShort120">Average Short Gain (120d)</option>
                        <option value="winRateShort120">Short Win Rate (120d)</option>
                    </optgroup>

                    <optgroup label="180-Day Metrics">
                        <option value="numTrades180">Number of Trades (180d)</option>
                        <option value="avgGain180">Average Gain (180d)</option>
                        <option value="winRate180">Win Rate (180d)</option>
                        <option value="numTradesLong180">Number of Long Trades (180d)</option>
                        <option value="avgGainLong180">Average Long Gain (180d)</option>
                        <option value="winRateLong180">Long Win Rate (180d)</option>
                        <option value="numTradesShort180">Number of Short Trades (180d)</option>
                        <option value="avgGainShort180">Average Short Gain (180d)</option>
                        <option value="winRateShort180">Short Win Rate (180d)</option>
                    </optgroup>

                    <optgroup label="365-Day Metrics">
                        <option value="numTrades365">Number of Trades (365d)</option>
                        <option value="avgGain365">Average Gain (365d)</option>
                        <option value="winRate365">Win Rate (365d)</option>
                        <option value="numTradesLong365">Number of Long Trades (365d)</option>
                        <option value="avgGainLong365">Average Long Gain (365d)</option>
                        <option value="winRateLong365">Long Win Rate (365d)</option>
                        <option value="numTradesShort365">Number of Short Trades (365d)</option>
                        <option value="avgGainShort365">Average Short Gain (365d)</option>
                        <option value="winRateShort365">Short Win Rate (365d)</option>
                    </optgroup>

                    <optgroup label="545-Day Metrics">
                        <option value="numTrades545">Number of Trades (545d)</option>
                        <option value="avgGain545">Average Gain (545d)</option>
                        <option value="winRate545">Win Rate (545d)</option>
                        <option value="numTradesLong545">Number of Long Trades (545d)</option>
                        <option value="avgGainLong545">Average Long Gain (545d)</option>
                        <option value="winRateLong545">Long Win Rate (545d)</option>
                        <option value="numTradesShort545">Number of Short Trades (545d)</option>
                        <option value="avgGainShort545">Average Short Gain (545d)</option>
                        <option value="winRateShort545">Short Win Rate (545d)</option>
                    </optgroup>

                    <optgroup label="730-Day Metrics">
                        <option value="numTrades730">Number of Trades (730d)</option>
                        <option value="avgGain730">Average Gain (730d)</option>
                        <option value="winRate730">Win Rate (730d)</option>
                        <option value="numTradesLong730">Number of Long Trades (730d)</option>
                        <option value="avgGainLong730">Average Long Gain (730d)</option>
                        <option value="winRateLong730">Long Win Rate (730d)</option>
                        <option value="numTradesShort730">Number of Short Trades (730d)</option>
                        <option value="avgGainShort730">Average Short Gain (730d)</option>
                        <option value="winRateShort730">Short Win Rate (730d)</option>
                    </optgroup>
                </select>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="backtestSortingOrder">
                    Sorting Order
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="backtestSortingOrder"
                    options={[
                        { value: 'asc', label: 'Ascending' },
                        { value: 'desc', label: 'Descending' }
                    ]}
                    value={newBacktestSortingRule.order}
                    onChange={(e) => setNewBacktestSortingRule((prev) => ({ ...prev, order: e.target.value as 'asc' | 'desc' }))}
                />
            </div>
        </Modal>
    );
};

export default BacktestSortingRuleModal;
